/**
 * TradingView setup UI — Strategy Builder (Phase 2).
 *
 * Shared by three render sites, all gated on EXTERNAL + TRADINGVIEW:
 * - TradingViewJsonActions  — compact block inside the External Signal Mode panel
 * - TradingViewGuideContent — setup guide body used in the desktop right
 *   column and inside the mobile BottomSheet ("How to connect TradingView")
 *
 * All JSON generation goes through tradingViewSetup.js helpers; copy feedback
 * goes through the shared useCopyFeedback hook (inline "Copied" flip + toast,
 * the same pattern the External Signal Mode panel already used).
 */
import { useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { toast } from '../../store';
import { cn } from '../../lib/utils';
import {
  TRADINGVIEW_ACTIONS,
  TRADINGVIEW_SAMPLE_PINE_URL,
  OPTIONS_BY_ALERT_LOT_EXAMPLES,
  buildTradingViewAlertJson,
} from './tradingViewSetup';

/** Shared clipboard helper: inline per-key "Copied" flip + toast feedback. */
function useCopyFeedback() {
  const [copiedKey, setCopiedKey] = useState(null);

  async function copy(value, key, successMessage) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      if (successMessage) toast.success(successMessage);
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }

  return { copiedKey, copy };
}

const actionButtonClass = 'px-2 py-1 text-2xs font-mono inline-flex items-center gap-1 rounded bg-info/20 text-info hover:bg-info/30 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Compact TradingView block for the External Signal Mode panel:
 * six plain-language action buttons (click = immediate JSON copy) plus the
 * sample Pine Script download.
 */
export function TradingViewJsonActions({ webhookToken }) {
  const { copiedKey, copy } = useCopyFeedback();

  return (
    <div className="pt-1 space-y-2">
      <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
        Choose an action to copy the ready-to-use TradingView JSON:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {TRADINGVIEW_ACTIONS.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            disabled={!webhookToken}
            title={webhookToken ? `Copy ${label} JSON` : 'Available once the Strategy Token is reserved'}
            className={actionButtonClass}
            onClick={() => copy(buildTradingViewAlertJson({ token: webhookToken, action: code }), `action-${code}`, `${label} JSON copied`)}
          >
            {copiedKey === `action-${code}` ? <Check size={10} className="text-bull" /> : <Copy size={10} />}
            {copiedKey === `action-${code}` ? 'Copied' : label}
          </button>
        ))}
      </div>
      <div>
        <a
          href={TRADINGVIEW_SAMPLE_PINE_URL}
          download
          className="inline-flex items-center gap-1 px-2 py-1 text-2xs font-mono rounded bg-info/20 text-info hover:bg-info/30"
        >
          <Download size={10} />
          Download Sample Pine Script
        </a>
      </div>
    </div>
  );
}

/**
 * TradingView Setup Guide body (heading is provided by the host: desktop card
 * or BottomSheet title). Tab and selected-action state stay local.
 */
export function TradingViewGuideContent({ webhookUrl, webhookToken, isOptionsByAlert = false }) {
  const [tab, setTab] = useState('normal');
  const [selectedCode, setSelectedCode] = useState('LE');
  const { copiedKey, copy } = useCopyFeedback();

  const selected = TRADINGVIEW_ACTIONS.find((a) => a.code === selectedCode) || TRADINGVIEW_ACTIONS[0];
  const previewJson = webhookToken ? buildTradingViewAlertJson({ token: webhookToken, action: selected.code }) : null;

  return (
    <div className="space-y-3">
      {/* Tabs — both panels stay mounted; the inactive one is hidden */}
      <div className="flex gap-1.5" role="group" aria-label="TradingView setup method">
        {[
          { key: 'normal', label: 'Normal Alert' },
          { key: 'pine',   label: 'Pine Script' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'px-2.5 py-1 text-2xs font-mono rounded border transition-colors',
              tab === key
                ? 'border-info/40 bg-info/15 text-info'
                : 'border-bg-border text-txt-muted hover:text-txt-secondary'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Normal Alert ── */}
      <div className={tab === 'normal' ? 'space-y-3' : 'hidden'}>
        <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
          Works with any normal TradingView alert — a price level, a line crossing, a drawing, or an indicator condition.
        </p>
        <ol className="list-decimal pl-4 space-y-1 text-2xs font-mono text-txt-secondary leading-relaxed">
          <li>Copy your Webhook URL.</li>
          <li>Create a TradingView alert.</li>
          <li>Enable Webhook URL and paste it.</li>
          <li>Choose the action below.</li>
          <li>Copy the JSON into the alert Message box.</li>
        </ol>
        <div className="flex items-center gap-2">
          <p className="text-2xs font-mono text-txt-primary break-all flex-1">{webhookUrl}</p>
          <button
            type="button"
            className={actionButtonClass}
            onClick={() => copy(webhookUrl, 'guide-url', 'Webhook URL copied')}
          >
            {copiedKey === 'guide-url' ? <Check size={10} className="text-bull" /> : <Copy size={10} />}
            {copiedKey === 'guide-url' ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {TRADINGVIEW_ACTIONS.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              aria-pressed={selected.code === code}
              onClick={() => setSelectedCode(code)}
              className={cn(
                'px-2 py-1 text-2xs font-mono rounded border transition-colors',
                selected.code === code
                  ? 'border-info/40 bg-info/15 text-info'
                  : 'border-bg-border text-txt-muted hover:text-txt-secondary'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {previewJson ? (
          <div className="space-y-1.5">
            <p className="text-2xs font-mono uppercase tracking-wide text-txt-muted">{selected.label} JSON</p>
            <pre className="rounded border border-bg-border bg-bg-surface/70 px-2 py-1.5 text-[11px] font-mono text-txt-secondary whitespace-pre-wrap break-all">{previewJson}</pre>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={actionButtonClass}
                onClick={() => copy(previewJson, 'guide-json', `${selected.label} JSON copied`)}
              >
                {copiedKey === 'guide-json' ? <Check size={10} className="text-bull" /> : <Copy size={10} />}
                {copiedKey === 'guide-json' ? 'Copied' : 'Copy JSON'}
              </button>
            </div>
            <p className="text-2xs font-mono text-warn leading-relaxed">
              Keep this JSON private because it contains your Strategy Token.
            </p>
          </div>
        ) : (
          <p className="text-2xs font-mono text-txt-muted leading-relaxed">
            Your Strategy Token is being reserved — the JSON preview will appear here.
          </p>
        )}

        <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
          &ldquo;Exit Long, then Short&rdquo; fully exits the current Long position before attempting the Short Entry.
          &ldquo;Exit Short, then Long&rdquo; fully exits the current Short position before attempting the Long Entry.
          The reversal is not completed atomically — the exit and the reverse entry are separate steps and are not guaranteed to complete together.
        </p>

        {isOptionsByAlert && (
          <div className="rounded-lg border border-warn/25 bg-warn/5 px-2.5 py-2 space-y-1.5">
            <p className="text-2xs font-mono text-warn font-medium">Options quantity (By Alert)</p>
            <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
              For Options strategies using By Alert quantity, the quantity in the TradingView JSON is the number of lots,
              not raw units. Broker/exchange quantity = alert lots × resolved contract lot size.
            </p>
            <ul className="list-disc pl-4 space-y-0.5 text-2xs font-mono text-txt-secondary leading-relaxed">
              {OPTIONS_BY_ALERT_LOT_EXAMPLES.map(({ alertLots, lotSize, brokerUnits }) => (
                <li key={alertLots}>
                  Alert quantity {alertLots} → {alertLots} {alertLots === 1 ? 'lot' : 'lots'} → broker quantity {brokerUnits.toLocaleString('en-IN')} units (lot size {lotSize})
                </li>
              ))}
            </ul>
            <p className="text-2xs font-mono text-txt-primary leading-relaxed">
              An Options alert quantity of 65 means 65 lots. It does not mean one lot merely because the contract lot size is also 65.
            </p>
            <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
              Entry and partial exit use the same unit: entry quantity is lots and partial-exit quantity is lots.
              An exit smaller than the open lots is a partial exit when Partial Exit is enabled, and the remaining lots stay open.
            </p>
          </div>
        )}

        <p className="text-2xs font-mono text-txt-muted leading-relaxed">
          Start in PAPER mode and test with one small alert. Verify the action, symbol, price, and quantity (lots for Options)
          before considering LIVE. PAPER success does not prove LIVE broker execution. Webhook accepted does not necessarily
          mean the broker order was filled.
        </p>
      </div>

      {/* ── Pine Script ── */}
      <div className={tab === 'pine' ? 'space-y-3' : 'hidden'}>
        <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
          Already using a Pine Script? Download the AlgoSys sample script and add your own Buy, Sell, and Exit conditions.
        </p>
        <div>
          <a
            href={TRADINGVIEW_SAMPLE_PINE_URL}
            download
            className="inline-flex items-center gap-1 px-2 py-1 text-2xs font-mono rounded bg-info/20 text-info hover:bg-info/30"
          >
            <Download size={10} />
            Download Sample Pine Script
          </a>
        </div>
        <ol className="list-decimal pl-4 space-y-1 text-2xs font-mono text-txt-secondary leading-relaxed">
          <li>Open the Pine Editor in TradingView.</li>
          <li>Paste the sample script.</li>
          <li>Enter your Strategy Token in the script inputs.</li>
          <li>Add the script to your chart.</li>
          <li>Create a TradingView alert and choose &ldquo;Any alert() function call&rdquo;.</li>
          <li>Enable the Webhook URL.</li>
        </ol>
      </div>
    </div>
  );
}
